import {
	AddressItemType,
	deleteAutoForwarding,
	getAutoForwardingSummary,
	ipmQueryClient,
	PostageUsageType,
	updateAutoForwarding,
	useAutoForwarding,
} from '@ukpostbox/client-data';
import {
	AutoForwardingDataType,
	AutoForwardingSummaryResponseType,
} from '@ukpostbox/client-data/lib/Types/AutoForwardingTypes';
import { IpmRqKeys } from '@ukpostbox/client-data/lib/Wrappers/ReactQuery/CacheKeys';
import {
	AlertBanner,
	Button,
	ButtonSubmit,
	Card,
	CardLayoutType,
	Colour,
	ColourStateSolid,
	ComboBoxControl,
	ComboBoxItemDisplayEnum,
	ComboBoxTypeEnum,
	ContentBlock,
	ContentLoader,
	FlexGap,
	StackVertical,
	ToggleSwitch,
	ToggleType,
	useTranslation,
} from '@ukpostbox/frontend-framework';
import { Controller, FieldValues, FormProvider, useForm } from '@ukpostbox/frontend-framework/lib/ReactHookForm';
import { FormatLocaleDate } from '@ukpostbox/helpers';
import React, { useEffect, useState } from 'react';
import { useSite } from '../../_Global/Providers/SiteInterfaceProvider';
import { AddressControl } from '../../_Global/Components/Address/AddressControl';
import { ErrorMapper } from '../../_Global/Components/ErrorMapper';
import { PostageControl } from '../../_Global/Components/PostageControl';
import AddTagControlMinimal from '../../_Global/Components/Tags/AddTagControlMinimal';

function AutoForwarding() {
	const { t } = useTranslation();

	const { currentLocale } = useSite();
	const { data, status, error } = useAutoForwarding();
	const [responseError, setError] = useState<any>(null);
	const [showTagControl, setShowTagControl] = useState<boolean>(data?.forwardingConditions.selectedValue === 1);

	const [resetForm, setResetForm] = useState<boolean>(false);

	const formMethods = useForm({
		defaultValues: {
			postageOptionId: data?.postageOptionId || -1,
			addressId: data?.addressId || -1,
			nextForwardingDate: data?.nextForwardingDate || new Date(),
			batchExistingItems: data?.batchExistingItems || false,
			forwardingConditions: data?.forwardingConditions.selectedValue || -1,
			forwardingFrequency: data?.forwardingFrequency.selectedValue || -1,
			forwardingConditionsValueId: data?.forwardingConditionsValueId || -1,
		},
		mode: 'onChange',
	});

	const [summaryData, setSummaryData] = useState<AutoForwardingSummaryResponseType | null>(null);
	const [activeAddress, setActiveAddress] = useState<AddressItemType | null>(null);

	const GetSummary = () => {
		getAutoForwardingSummary().then((r) => {
			setSummaryData(r);
		});
	};

	const HandleDelete = () => {
		deleteAutoForwarding().then((r) => {
			ipmQueryClient
				.invalidateQueries({ queryKey: [IpmRqKeys.ACCOUNT_SETTINGS_AUTO_FORWARDING_KEY] })
				.then(() => {
					formMethods.reset();
					setResetForm(!resetForm);
					setShowTagControl(false);
				});
		});
	};

	const UpdateDetails = (formData: FieldValues) => {
		updateAutoForwarding(formData as AutoForwardingDataType)
			.then((r) => setError(null))
			.catch((e) => setError(e));
	};

	useEffect(() => {
		const subscription = formMethods.watch((e) => {
			console.log('watch', e);
			console.log(formMethods.formState);
		});

		return () => subscription.unsubscribe();
	}, [formMethods.watch]);

	useEffect(() => {
		if (data && data.addressId) {
			GetSummary();
			setShowTagControl(data.forwardingConditions.selectedValue === 1);

			formMethods.setValue('addressId', data.addressId || -1);
			formMethods.setValue('postageOptionId', data.postageOptionId || -1);
			formMethods.setValue('nextForwardingDate', data.nextForwardingDate || new Date());
			formMethods.setValue('batchExistingItems', data.batchExistingItems || false);
			formMethods.setValue('forwardingConditions', data.forwardingConditions.selectedValue || -1);
			formMethods.setValue('forwardingFrequency', data.forwardingFrequency.selectedValue || -1);
			formMethods.setValue('forwardingConditionsValueId', data.forwardingConditionsValueId || -1);
		}
	}, [data]);

	return status === 'pending' ? (
		<ContentLoader childHeight='6em' childCount={6} />
	) : status === 'error' ? (
		<ContentBlock error title={error.name} content={error.message} />
	) : (
		<FormProvider {...formMethods}>
			<form onSubmit={formMethods.handleSubmit((formData: FieldValues) => UpdateDetails(formData))}>
				<fieldset>
					<Card
						header={{ title: t('settings.sections.autoForwarding') }}
						type={CardLayoutType.SlimHeader}
						emptyBody
					>
						{summaryData && summaryData.nextForwardingDate && (
							<div className='p-md'>
								<AlertBanner
									title={t('settings.autoForwarding.labels.nextRun')}
									content={t('settings.autoForwarding.messages.nextRun', {
										count: summaryData.itemsInNextBatch,
										date: FormatLocaleDate(summaryData.nextForwardingDate, currentLocale),
									})}
									colour={Colour.Primary}
								/>
							</div>
						)}

						{responseError && (
							<div className='p-md'>
								<ErrorMapper title={responseError?.name} errorResponse={responseError} />
							</div>
						)}

						<div className='user-action'>
							<div className='fade'>{t('settings.autoForwarding.labels.destinationAddress')}</div>
							<div>
								<Controller
									control={formMethods.control}
									name='addressId'
									rules={{ min: 0 }}
									render={({ field }) => (
										<AddressControl
											name={field.name}
											showCreateAddressOptions
											singleSelection
											forwardItemCallback={(e) => {
												setActiveAddress(e);
												field.onChange(e.value);
											}}
											defaultValues={field.value !== -1 ? [field.value] : undefined}
											minimal
											required
											reset={resetForm}
										/>
									)}
								/>
							</div>
						</div>
						<div className='user-action'>
							<div className='fade'>{t('settings.autoForwarding.labels.postageOption')}</div>
							<div>
								<Controller
									control={formMethods.control}
									name='postageOptionId'
									rules={{ min: 0 }}
									render={({ field }) => (
										<PostageControl
											postageType={PostageUsageType.Forwarding}
											name={field.name}
											activeAddress={activeAddress}
											alwaysEnabled
											singleSelection
											forwardValueCallback={(e) => {
												field.onChange(e);
											}}
											defaultValues={field.value !== -1 ? [field.value] : undefined}
											minimal
											required
											reset={resetForm}
										/>
									)}
								/>
							</div>
						</div>
						<div className='user-action'>
							<div className='fade'>{t('settings.autoForwarding.labels.forwardingConditions')}</div>
							<div>
								<Controller
									control={formMethods.control}
									name='forwardingConditions'
									rules={{ min: 0 }}
									render={({ field }) => (
										<ComboBoxControl
											name={field.name}
											placeholder={t(
												'settings.autoForwarding.controls.forwardingConditionsPlaceholder',
											)}
											data={data?.forwardingConditions.options}
											forwardValueCallback={(e) => {
												setShowTagControl(e === 1);
												field.onChange(e);
											}}
											valueType='number'
											defaultValue={field.value !== -1 ? [field.value] : undefined}
											displayType={ComboBoxTypeEnum.Single}
											displayItemLabelType={ComboBoxItemDisplayEnum.Label}
											formReset={resetForm}
										/>
									)}
								/>
							</div>
						</div>

						{showTagControl && (
							<div className='user-action'>
								<div className='fade'>{t('settings.autoForwarding.labels.matchingTags')}</div>
								<div>
									<Controller
										control={formMethods.control}
										name='forwardingConditionsValueId'
										rules={{ min: 0 }}
										render={({ field }) => (
											<AddTagControlMinimal
												name={field.name}
												minimal
												existingTags={
													field.value !== -1 ? ([field.value] as Array<number>) : undefined
												}
												updateCallback={(e: any) => field.onChange(e)}
												singleSelection
												reset={resetForm}
											/>
										)}
									/>
								</div>
							</div>
						)}

						<div className='user-action'>
							<div className='fade'>{t('settings.autoForwarding.labels.nextForwardDate')}</div>
							<div>
								{/*                                        <Controller
                                            control={formMethods.control}
                                            name={'nextForwardingDate'}
                                            render={({ field }) => {
                                                console.log('datepicker', field.value);
                                                return (
                                                    <DatePickerControl
                                                        singleDate={true}
                                                        locale={currentLocale}
                                                        defaultStartDate={field.value ? new Date(field.value) : new Date()}
                                                        forwardValueCallback={(e) => field.onChange(e)}
                                                        name={'nextForwardingDate'}
                                                    />
                                                )
                                            }
                                            }
                                        /> */}
							</div>
						</div>
						<div className='user-action'>
							<div className='fade'>{t('settings.autoForwarding.labels.forwardingFrequency')}</div>
							<div>
								<Controller
									control={formMethods.control}
									name='forwardingFrequency'
									rules={{ min: 0 }}
									render={({ field }) => (
										<ComboBoxControl
											name={field.name}
											placeholder={t(
												'settings.autoForwarding.controls.forwardingFrequencyPlaceholder',
											)}
											data={data?.forwardingFrequency.options}
											forwardValueCallback={(e) => {
												field.onChange(e);
											}}
											valueType='number'
											defaultValue={field.value !== -1 ? [field.value] : undefined}
											displayType={ComboBoxTypeEnum.Single}
											displayItemLabelType={ComboBoxItemDisplayEnum.Label}
											formReset={resetForm}
										/>
									)}
								/>
							</div>
						</div>
						<div className='user-action'>
							<div className='fade'>{t('settings.autoForwarding.labels.batchExistingItems')}</div>
							<div>
								<Controller
									control={formMethods.control}
									name='batchExistingItems'
									rules={{ min: 0 }}
									render={({ field }) => (
										<ToggleSwitch
											name={field.name}
											label={t('settings.autoForwarding.labels.batchExistingItems')}
											type={ToggleType.Switch}
											defaultChecked={field.value}
											onChange={(e) => field.onChange(e.currentTarget.checked)}
										/>
									)}
								/>
							</div>
						</div>

						<StackVertical gap={FlexGap.ExtraSmall} extraClass='p-md'>
							<ButtonSubmit
								title={t('settings.autoForwarding.actions.save')}
								fullWidth
								colour={ColourStateSolid.Primary}
								loading={formMethods.formState.isSubmitting}
								disabled={formMethods.formState.isSubmitting || !formMethods.formState.isValid}
							/>
							<Button
								title={t('settings.autoForwarding.actions.reset')}
								fullWidth
								onClick={() => HandleDelete()}
							/>
						</StackVertical>
					</Card>
				</fieldset>
			</form>
		</FormProvider>
	);
}

export { AutoForwarding };
